import { useEffect, useState } from "react"
import { aHexToRgba, rgbaToHex } from "hex-and-rgba"
import { intComma } from "humanize-plus"
import classnames from "classnames"
import Spinner from "shared/components/spinner"
import { formatHexColorValue } from "shared/lib/alternate_brand_color"
import ConnectedServicesChart from "./connected_services_donut_chart"
import { fetchOverview } from "./api"
import { OverviewMetrics } from "./types"

import styles from "./service_analytics.module.scss"

const Overview = ({
  serviceModuleName,
  serviceName,
  servicesUrl,
}: {
  serviceModuleName: string
  serviceName: string
  servicesUrl: string
}) => {
  const [loading, setLoading] = useState(true)
  const [metrics, setMetrics] = useState({} as OverviewMetrics)
  const [brandColors, setBrandColors] = useState({ regular: "", dark: "" })

  useEffect(() => {
    fetchOverview(serviceModuleName)
      .then(({ simple_service_metrics }: { simple_service_metrics: OverviewMetrics }) => {
        setMetrics(simple_service_metrics)
        const brandColor = formatHexColorValue(simple_service_metrics.channel.brand_color)
        const [r, g, b, a] = aHexToRgba(brandColor)
        const darkerBrandColor: string = rgbaToHex(...[r, g, b].map(c => Math.floor(c * 0.8)), a)
        setBrandColors({ regular: brandColor, dark: darkerBrandColor })
      })
      .catch(() => {
        window.appendFlash("Failed to fetch analytics metrics for this service", "danger")
      })
      .finally(() => setLoading(false))
  }, [])

  return loading ? (
    <Spinner />
  ) : (
    <div className={styles.overview}>
      <div className={styles.module}>
        <div className={classnames(styles["stat-box"], styles["negative-text"])} style={{ backgroundColor: brandColors.regular }}>
          <p className="txt-body-3">Connected users</p>
          <h3>{intComma(metrics.overview_metrics?.connected_users)}</h3>
        </div>
        <div className={classnames(styles["stat-box"], styles["negative-text"])} style={{ backgroundColor: brandColors.dark }}>
          <p className="txt-body-3">Users with enabled Applets</p>
          <h3>{intComma(metrics.overview_metrics?.users_with_enabled_applets)}</h3>
        </div>
        <div className={styles["stat-box"]}>
          <p className="txt-body-3">{serviceName} is connected to</p>
          <h3>{intComma(metrics.overview_metrics?.connected_services)} services</h3>
        </div>
      </div>
      <div className={styles["services-chart"]}>
        <ConnectedServicesChart
          worksWith={metrics.works_with_metrics}
          serviceName={serviceName}
          servicesUrl={servicesUrl}
        />
        <div className={styles["top-services"]}>
          <h3>Top 3 services</h3>
          <ul>
            {metrics.works_with_metrics
              ?.sort((a, b) => b.users_with_enabled_applets - a.users_with_enabled_applets)
              ?.slice(0, 3)
              ?.map(({ works_with_channel }) => (
                <li key={works_with_channel.module_name}>
                  <img src={works_with_channel.monochrome_image_url} alt={works_with_channel.name} />
                  <p>{works_with_channel.name}</p>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default Overview
